"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Move } from "lucide-react";

type Tbl = { id: string; x: number; y: number; w: number; h: number; min?: string };

const TABLES: Tbl[] = [
  { id: "VIP 1", x: 5, y: 12, w: 14, h: 18, min: "$2K" },
  { id: "VIP 2", x: 22, y: 12, w: 14, h: 18, min: "$2K" },
  { id: "VIP 3", x: 39, y: 12, w: 14, h: 18, min: "$3K" },
  { id: "VIP 4", x: 56, y: 12, w: 14, h: 18, min: "$3K" },
  { id: "VIP 5", x: 73, y: 12, w: 18, h: 24, min: "$5K" },
  { id: "T-12", x: 5, y: 38, w: 10, h: 14 },
  { id: "T-14", x: 18, y: 38, w: 10, h: 14 },
  { id: "T-15", x: 31, y: 38, w: 10, h: 14 },
  { id: "T-17", x: 5, y: 58, w: 10, h: 14 },
  { id: "T-18", x: 18, y: 58, w: 10, h: 14 },
  { id: "Bar", x: 50, y: 56, w: 40, h: 8 },
  { id: "Stage", x: 30, y: 82, w: 35, h: 12 },
];

export default function VipTableMapEditorScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
                {t("Floor plan editor", "Editor de floor plan")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6">
                {t("Draw it. ", "Dibújalo. ")}
                <span className="text-white/30">{t("Once.", "Una sola vez.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "Drag tables onto a canvas. Set minimum spend per table. Mark the bar and stage. Minutes from blank to live floor.",
                  "Arrastra mesas sobre un canvas. Define mínimo de gasto por mesa. Marca la barra y el escenario. Minutos de cero a floor en vivo."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white/[0.04] border border-white/10 px-4 py-3 rounded-full">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(244,114,182,0.15)" }}>
                  <Move className="w-3.5 h-3.5" style={{ color: "#F472B6" }} />
                </div>
                <div className="text-[13px] font-bold text-white">
                  {t("Drag · Drop · Save", "Arrastra · Suelta · Guarda")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Floor plan editor mockup */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-5 md:p-6"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                  {t("Floor — Main room", "Floor — Salón principal")}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-white/55">
                  <span>12 {t("tables", "mesas")}</span>
                  <span>·</span>
                  <span>{t("Saved", "Guardado")}</span>
                </div>
              </div>

              {/* Canvas */}
              <div
                className="relative aspect-[4/3] rounded-[14px] overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(124,58,237,0.06) 0%, rgba(244,114,182,0.04) 100%)",
                  border: "1px dashed rgba(255,255,255,0.10)",
                }}
              >
                {/* Grid background */}
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
                    backgroundSize: "24px 24px",
                  }}
                />

                {/* Tables */}
                {TABLES.map((tbl, i) => {
                  const isVip = tbl.id.startsWith("VIP");
                  const isBar = tbl.id === "Bar";
                  const isStage = tbl.id === "Stage";
                  return (
                    <motion.div
                      key={tbl.id}
                      initial={{ opacity: 0, scale: 0.6 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{
                        duration: 0.4,
                        delay: 0.2 + 0.05 * i,
                        ease: [0.21, 0.47, 0.32, 0.98],
                      }}
                      className="absolute rounded-md flex flex-col items-center justify-center text-center"
                      style={{
                        left: `${tbl.x}%`,
                        top: `${tbl.y}%`,
                        width: `${tbl.w}%`,
                        height: `${tbl.h}%`,
                        backgroundColor: isBar
                          ? "rgba(251,146,60,0.20)"
                          : isStage
                            ? "rgba(255,255,255,0.06)"
                            : isVip
                              ? "rgba(124,58,237,0.20)"
                              : "rgba(255,255,255,0.06)",
                        border: isBar
                          ? "1px solid rgba(251,146,60,0.4)"
                          : isStage
                            ? "1px dashed rgba(255,255,255,0.25)"
                            : isVip
                              ? "1px solid rgba(196,181,253,0.5)"
                              : "1px solid rgba(255,255,255,0.15)",
                      }}
                    >
                      <div
                        className="text-[9px] font-black uppercase tracking-wider leading-none"
                        style={{
                          color: isBar
                            ? "#FB923C"
                            : isStage
                              ? "rgba(255,255,255,0.55)"
                              : isVip
                                ? "#C4B5FD"
                                : "rgba(255,255,255,0.75)",
                        }}
                      >
                        {tbl.id}
                      </div>
                      {tbl.min && (
                        <div className="text-[8px] font-bold text-white/55 mt-0.5">
                          MIN {tbl.min}
                        </div>
                      )}
                    </motion.div>
                  );
                })}

                {/* Toolbar */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 flex items-center gap-2">
                  <div className="text-[9px] font-bold text-white/70">+ Table</div>
                  <div className="w-px h-3 bg-white/15" />
                  <div className="text-[9px] font-bold text-white/70">+ VIP</div>
                  <div className="w-px h-3 bg-white/15" />
                  <div className="text-[9px] font-bold text-white/70">+ Bar</div>
                </div>
              </div>

              <div className="mt-3 text-[11px] font-medium text-white/45 text-center">
                {t("Drag any table to move. Tap to set minimum or rename.", "Arrastra cualquier mesa para mover. Toca para definir mínimo o renombrar.")}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
